import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Scale } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import api from "@/lib/api";
import { VendorCard } from "@/components/discovery/VendorCard";
import { LAWYER_PRACTICE_AREAS, LAWYER_CITIES, breadcrumbLd } from "@/lib/siteContent";

const CAT = "lawyers-and-legal-services";

export default function Lawyers() {
  const { city } = useParams();
  const [vendors, setVendors] = useState([]);
  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : null;

  useEffect(() => {
    const p = new URLSearchParams({ category: CAT, limit: "12" });
    if (cityName) p.set("city", cityName);
    api.get(`/discovery/vendors?${p.toString()}`).then((r) => setVendors(r.data.items)).catch(() => {});
  }, [cityName]);

  const crumbs = city
    ? [{ label: "Home", to: "/" }, { label: "Find a Lawyer", to: "/lawyers" }, { label: cityName, to: `/lawyers/${city}` }]
    : [{ label: "Home", to: "/" }, { label: "Find a Lawyer", to: "/lawyers" }];

  return (
    <div className="ca-container py-12">
      <Seo title={city ? `Find a Lawyer in ${cityName} | ComplaintAdvisor` : "Find a Lawyer by Practice Area and City | ComplaintAdvisor"}
        description={city
          ? `Explore lawyer profiles in ${cityName} by practice area. Review publicly provided professional information and contact lawyers directly for independent legal advice.`
          : "Explore lawyer profiles by practice area and city. Review publicly provided professional information and contact lawyers directly for independent legal advice."}
        path={city ? `/lawyers/${city}` : "/lawyers"} jsonld={breadcrumbLd(crumbs)} />
      <Breadcrumbs items={crumbs} />
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-3 flex items-center gap-2"><Scale className="h-7 w-7 text-teal" /> {city ? `Find a Lawyer in ${cityName}` : "Find a Lawyer"}</h1>
      <p className="text-slate-700 mt-2 max-w-2xl">
        ComplaintAdvisor provides a directory of legal professionals using publicly available or consent-based professional
        information. Listing information does not constitute an endorsement or guarantee of outcome.
      </p>

      <div className="mt-6">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Practice areas</p>
        <div className="flex flex-wrap gap-2">{LAWYER_PRACTICE_AREAS.map((a) => <span key={a} className="rounded-full bg-slate-100 text-slate-600 px-3 py-1 text-xs">{a}</span>)}</div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Browse by city</p>
        <div className="flex flex-wrap gap-2">
          {LAWYER_CITIES.map((c) => (
            <Link key={c} to={`/lawyers/${c.toLowerCase()}`} data-testid={`lawyer-city-${c.toLowerCase()}`}
              className={`rounded-full px-3 py-1 text-xs ${cityName === c ? "bg-teal-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-teal-brand/10 hover:text-teal"}`}>{c}</Link>
          ))}
        </div>
      </div>

      <h2 className="font-display text-lg font-bold text-slate-900 mt-10">{city ? `Lawyers in ${cityName}` : "Legal service providers"}</h2>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {vendors.map((v) => <VendorCard key={v.slug} v={v} />)}
        {!vendors.length && <p className="text-sm text-slate-400">No profiles available{city ? ` in ${cityName}` : ""} yet.</p>}
      </div>

      <p className="text-xs text-slate-400 mt-8">Users should independently verify credentials and obtain advice directly from the lawyer. Contact details are shown only with consent. Need help? <Link to="/contact" className="text-teal">Contact us</Link>.</p>
    </div>
  );
}
